@let selectedMenu = menu();

<article class="booking-upsell-step-card">
    <header class="booking-upsell-step-header vstack gap-1">
        <h2
            #heading
            class="booking-step-heading h4 fw-semibold m-0"
            tabindex="-1"
        >
            Tell us what you'd like
        </h2>
        @if (selectedMenu) {
            <p class="text-secondary mb-0">
                {{ menuDescription() }}
            </p>
        }
        @if (requiredQuantity() !== null) {
            <p class="mb-0" aria-live="polite">Choose {{ requiredQuantity() }} items in total, one per guest. {{ totalQuantity() }} selected.</p>
        }
    </header>

    @if (selectedMenu) {
        <div class="booking-upsell-content vstack gap-3">
            @if (items().length > 0) {
                <div
                    class="booking-upsell-list vstack gap-3"
                    role="list"
                    aria-label="Available extras"
                >
                    @for (item of items(); track item.menuItemId) {
                        <article
                            class="booking-upsell-item"
                            [class.booking-upsell-item--with-image]="item.imageId"
                            role="listitem"
                        >
                            @if (item.imageId) {
                                <img
                                    class="booking-upsell-item-image"
                                    [ngSrc]="productImageBaseUrl + item.imageId"
                                    alt=""
                                    width="92"
                                    height="92"
                                    loading="lazy"
                                >
                            }
                            <div class="booking-upsell-item-content vstack gap-2">
                                <h3 class="h6 fw-semibold mb-0">{{ item.name }}</h3>
                                @if (showPrices() && item.paidOnline !== false) {
                                    <span data-testid="upsell-price">{{ null | currencyUnit : item.amount : availability().venue.currencyCode }}</span>
                                    <small class="text-secondary" data-testid="upsell-refund-policy">{{ item.refundable === false ? 'Non-refundable' : 'Refundable before the booking’s refund deadline' }}</small>
                                }

                                @if (item.description) {
                                    <div class="small text-secondary">
                                        <div
                                            class="booking-upsell-item-description"
                                            [innerHTML]="descriptionContent(item)"
                                        ></div>
                                        @if (descriptionCanExpand(item)) {
                                            <button
                                                type="button"
                                                class="btn btn-link btn-sm p-0 text-decoration-none"
                                                [attr.aria-expanded]="descriptionIsExpanded(item)"
                                                (click)="toggleDescription(item)"
                                            >
                                                {{ descriptionIsExpanded(item) ? 'Read less' : 'Read more' }}
                                            </button>
                                        }
                                    </div>
                                }
                            </div>

                            <div
                                class="booking-upsell-quantity d-flex align-items-center"
                                role="group"
                                [attr.aria-label]="'Quantity for ' + item.name"
                            >
                                <button
                                    type="button"
                                    class="booking-upsell-quantity-button btn"
                                    [attr.aria-label]="'Remove one ' + item.name"
                                    [attr.data-testid]="'decrement-' + item.skuId"
                                    [disabled]="quantity(item) === 0"
                                    (click)="decrement(item)"
                                >
                                    <i class="bi bi-dash" aria-hidden="true"></i>
                                </button>
                                <span
                                    class="booking-upsell-quantity-value fw-semibold text-center"
                                    [attr.data-testid]="'quantity-' + item.skuId"
                                    aria-live="polite"
                                >
                                    {{ quantity(item) }}
                                </span>
                                <button
                                    type="button"
                                    class="booking-upsell-quantity-button btn"
                                    [attr.aria-label]="'Add one ' + item.name"
                                    [attr.data-testid]="'increment-' + item.skuId"
                                    [disabled]="!canIncrement()"
                                    (click)="increment(item)"
                                >
                                    <i class="bi bi-plus" aria-hidden="true"></i>
                                </button>
                            </div>
                            @if (item.modifierGroups.length > 0 && quantity(item) > 0) {
                                <div class="booking-upsell-modifiers vstack gap-3">
                                    @for (selection of selections(item); track $index; let selectionIndex = $index) {
                                        <section
                                            class="booking-upsell-selection vstack gap-2"
                                            [attr.aria-label]="item.name + ' ' + (selectionIndex + 1)"
                                        >
                                            @if (quantity(item) > 1) {
                                                <div class="hstack justify-content-between gap-2">
                                                    <h4 class="h6 mb-0 flex-grow-1">{{ item.name }} {{ selectionIndex + 1 }}</h4>
                                                    <button
                                                        type="button"
                                                        class="booking-upsell-selection-remove btn btn-link text-danger"
                                                        [attr.aria-label]="'Remove ' + item.name + ' ' + (selectionIndex + 1)"
                                                        (click)="removeSelection(item, selectionIndex)"
                                                    >
                                                        <i class="bi bi-trash fs-5" aria-hidden="true"></i>
                                                    </button>
                                                </div>
                                            }
                                            @for (group of item.modifierGroups; track group.id) {
                                                @let groupId = item.menuItemId + '-' + selectionIndex + '-' + group.id;
                                                @let error = submitted() ? modifierGroupError(selection, group) : null;
                                                <fieldset
                                                    class="booking-upsell-modifier-group"
                                                    [attr.aria-describedby]="groupId"
                                                    [attr.aria-invalid]="error ? 'true' : null"
                                                >
                                                    <legend class="booking-upsell-modifier-heading small fw-semibold mb-1">
                                                        <span>{{ group.name }}</span>
                                                        @if (modifierGroupLimits(group); as limits) {
                                                            <span class="text-secondary fw-normal">{{ limits }}</span>
                                                        }
                                                    </legend>
                                                    <div [id]="groupId">
                                                        @if (error) {
                                                            <p class="small text-danger mb-1" role="alert">{{ error }}</p>
                                                        }
                                                    </div>
                                                    <div class="booking-upsell-modifier-options">
                                                        @for (modifier of group.modifiers; track modifier.skuId) {
                                                            <label
                                                                class="booking-upsell-modifier"
                                                                [class.text-secondary]="modifierDisabled(selection, group, modifier.skuId)"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    class="form-check-input"
                                                                    [attr.aria-label]="modifier.name"
                                                                    [checked]="selection.modifierSkuIds?.includes(modifier.skuId) ?? false"
                                                                    [disabled]="modifierDisabled(selection, group, modifier.skuId)"
                                                                    (change)="toggleModifier(item, selectionIndex, group, modifier.skuId)"
                                                                />
                                                                <span>
                                                                    {{ modifier.name }}
                                                                    @if (showPrices() && item.paidOnline !== false && modifier.amount !== 0) {
                                                                        <small>({{ null | currencyUnit : modifier.amount : availability().venue.currencyCode }})</small>
                                                                    }
                                                                    @if (modifier.description) {
                                                                        <small class="d-block text-secondary">{{ modifier.description }}</small>
                                                                    }
                                                                </span>
                                                            </label>
                                                        } @empty {
                                                            <p class="small text-secondary mb-0">No options are currently available.</p>
                                                        }
                                                    </div>
                                                </fieldset>
                                            }
                                        </section>
                                    }
                                </div>
                            }
                        </article>
                    }
                </div>
            } @else {
                <div class="booking-upsell-empty-card text-center fw-semibold">
                    No add-ons are available for this booking.
                </div>
            }

            <button
                type="button"
                class="btn btn-primary w-100"
                data-testid="confirm-booking-upsells"
                [disabled]="!validQuantity()"
                (click)="confirm()"
            >
                Continue
            </button>
        </div>
    } @else {
        <div class="booking-upsell-content">
            <div
                class="booking-upsell-empty-card text-center fw-semibold"
                role="status"
            >
                No add-ons are available for this booking.
            </div>
        </div>
    }
</article>
